import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { contentAPI, GeneratedContent } from '../services/contentAPI';
import { theme } from '../styles/theme';
import ReadOnlyJoditEditor from '../components/ReadOnlyJoditEditor';

const PageWrapper = styled.div`
  min-height: 100vh;
  background-color: ${theme.colors.primary};
`;

const Container = styled.div`
  display: flex;
  gap: 2rem;
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem;
  
  @media (max-width: 1024px) {
    flex-direction: column;
    gap: 1rem;
  }
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  color: #2d3748;
  padding: 0.75rem 1.5rem;
  border-radius: 12px;
  cursor: pointer;
  margin-bottom: 2rem;
  font-weight: 600;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

  &:hover {
    background: white;
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;
  font-size: 1.2rem;
  color: white;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: white;
  background-color: rgba(255, 107, 107, 0.2);
  border: 1px solid rgba(255, 107, 107, 0.5);
  border-radius: 8px;
  margin: 2rem 0;
`;

const Header = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: 3rem;
  margin-bottom: 2rem;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.2);
  
  @media (max-width: 768px) {
    padding: 2rem;
  }
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  color: rgba(255, 255, 255, 0.95);
  line-height: 1.2;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1rem;
  align-items: center;
`;

const CategoryBadge = styled.span`
  background: linear-gradient(135deg, #FF6900 0%, #FF8A00 100%);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;


const CreatedAt = styled.span`
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.875rem;
  font-weight: 500;
`;

const Keywords = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const KeywordTag = styled.span`
  background: rgba(255, 255, 255, 0.15);
  color: rgba(255, 255, 255, 0.9);
  padding: 0.5rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  font-weight: 500;
`;

const MetaDescription = styled.p`
  color: rgba(255, 255, 255, 0.9);
  font-style: italic;
  margin-top: 1.5rem;
  font-size: 1.125rem;
  line-height: 1.6;
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  border-left: 4px solid #FF6900;
  backdrop-filter: blur(10px);
`;

const AuthorSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
`;

const AuthorImage = styled.img`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid #FF6900;
`;

const AuthorInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const AuthorName = styled.span`
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
  font-size: 1rem;
`;

const AuthorRole = styled.span`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.875rem;
`;

const ContentWrapper = styled.div`
  background: white;
  border-radius: 20px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  padding: 3rem;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 2rem;
  }
`;

const Content = styled.div`
  /* CSS Reset - 모든 기본 스타일 제거 */
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
    border: none;
    outline: none;
    background: transparent;
    font-family: inherit;
    font-size: inherit;
    font-weight: inherit;
    line-height: inherit;
    color: inherit;
    text-decoration: none;
    list-style: none;
  }

  /* 에디터와 동일한 기본 스타일만 적용 */
  font-family: '맑은 고딕', 'Malgun Gothic', sans-serif !important;
  font-size: 14px !important;
  line-height: 1.6 !important;
  color: #333 !important;
  background: white !important;
  padding: 3rem;
  border-radius: 20px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  
  @media (max-width: 768px) {
    padding: 2rem;
  }
  
  /* 에디터와 동일한 헤딩 스타일 */
  h1, h2, h3, h4, h5, h6 {
    font-family: '맑은 고딕', 'Malgun Gothic', sans-serif !important;
    margin: 16px 0 8px 0 !important;
    font-weight: 600 !important;
    color: #333 !important;
    background: transparent !important;
    border: none !important;
    padding: 0 !important;
    text-shadow: none !important;
    box-shadow: none !important;
  }
  
  h1 {
    font-size: 1.875rem !important;

    &:first-child {
      margin-top: 0 !important;
    }
  }
  
  h2 {
    font-size: 1.5rem !important;

    &:first-child {
      margin-top: 0 !important;
    }
  }
  
  h3 {
    font-size: 1.25rem !important;
  }
  
  /* 에디터와 동일한 문단 스타일 */
  p {
    margin: 8px 0 !important;
    color: #333 !important;
    font-size: 14px !important;
    line-height: 1.6 !important;
    background: transparent !important;
    padding: 0 !important;
  }
  
  /* 에디터와 동일한 목록 스타일 */
  ul, ol {
    margin: 8px 0 !important;
    padding-left: 24px !important;
    list-style: revert !important;
    background: transparent !important;

    li {
      margin-bottom: 0 !important;
      color: #333 !important;
      font-size: 14px !important;
      line-height: 1.6 !important;
      background: transparent !important;
      padding: 0 !important;
    }
  }
  
  /* 에디터와 동일한 인용문 스타일 */
  blockquote {
    border-left: 4px solid #F68D2E !important;
    border: 1px solid #F68D2E !important;
    margin: 16px 0 !important;
    padding: 8px 16px !important;
    background: white !important;
    font-style: normal !important;
    color: #333 !important;
    border-radius: 0 !important;
    box-shadow: none !important;
    backdrop-filter: none !important;
  }
  
  /* 에디터와 동일한 테이블 스타일 */
  table {
    border-collapse: collapse !important;
    width: 100% !important;
    margin: 16px 0 !important;
    background: transparent !important;

    th, td {
      border: 1px solid #e5e7eb !important;
      padding: 8px 12px !important;
      text-align: left !important;
      color: #333 !important;
      font-size: 14px !important;
      line-height: 1.6 !important;
    }

    th {
      background: #f8fafc !important;
      font-weight: 600 !important;
    }
  }
  
  /* 에디터와 동일한 이미지 스타일 */
  .image-container {
    margin: 8px 0;
    text-align: center;
    
    img {
      max-width: 100%;
      height: auto;
      border-radius: 4px;
      cursor: pointer;
      transition: all 0.3s ease;
      
      &:hover {
        transform: scale(1.02);
      }
    }
  }
  
  img {
    max-width: 100% !important;
    height: auto !important;
    border-radius: 4px !important;
    margin: 8px 0 !important;
    display: block !important;
    cursor: pointer !important;
    transition: all 0.3s ease !important;
    border: none !important;
    box-shadow: none !important;

    &:hover {
      transform: scale(1.02) !important;
    }
  }
  
  /* 에디터와 동일한 링크 스타일 */
  a {
    color: #667eea !important;
    text-decoration: none !important;
    background: transparent !important;
    border: none !important;
    padding: 0 !important;

    &:hover {
      text-decoration: underline !important;
    }
  }
  
  /* 에디터와 동일한 코드 스타일 */
  code {
    background: #f1f5f9 !important;
    padding: 2px 4px !important;
    border-radius: 3px !important;
    font-family: 'Courier New', monospace !important;
    font-size: 0.9em !important;
    color: #333 !important;
    border: none !important;
  }
  
  pre {
    background: #f1f5f9 !important;
    padding: 12px !important;
    border-radius: 6px !important;
    overflow-x: auto !important;
    margin: 16px 0 !important;
    border: none !important;

    code {
      background: none !important;
      padding: 0 !important;
    }
  }
`;



interface TocItem {
  id: string;
  text: string;
  level: number;
}

const BlogDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [content, setContent] = useState<GeneratedContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<GeneratedContent[]>([]);
  const [toc, setToc] = useState<TocItem[]>([]);
  const [activeHeading, setActiveHeading] = useState<string>('');
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchContent = async () => {
      if (!id) {
        setError('잘못된 블로그 ID입니다.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const data = await contentAPI.getContent(parseInt(id));
        console.log('블로그 콘텐츠 데이터:', {
          title: data.title,
          contentLength: data.content?.length,
          contentPreview: data.content?.substring(0, 200),
          hasHeadings: data.content?.includes('<h')
        });
        setContent(data);
        
        // 관련 포스트 가져오기
        const allContents = await contentAPI.getContents();
        const filtered = allContents
          .filter(post => post.id !== data.id)
          .sort((a, b) => {
            // 같은 카테고리 우선, 그 다음 최신순
            if (a.category.id === data.category.id && b.category.id !== data.category.id) return -1;
            if (b.category.id === data.category.id && a.category.id !== data.category.id) return 1;
            return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
          });
        setRelatedPosts(filtered.slice(0, 5));
      } catch (err) {
        setError('블로그 글을 불러오는데 실패했습니다.');
        console.error('Error fetching content:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, [id]);

  // 목차 생성 - 렌더링된 Jodit 에디터에서 헤딩 추출
  useEffect(() => {
    console.log('목차 생성 useEffect 실행됨', { hasContent: !!content?.content, hasContentRef: !!contentRef.current });

    if (content?.content) {
      const generateTocFromRenderedContent = () => {
        const tocItems: TocItem[] = [];

        console.log('목차 생성 함수 실행, 500ms 대기 시작');

        // 에디터가 렌더링될 때까지 잠시 대기
        setTimeout(() => {
          console.log('1000ms 대기 완료, DOM 요소 검색 시작');

          // 여러 방법으로 DOM 요소 찾기
          let targetElement = null;

          // 방법 1: contentRef 사용
          if (contentRef.current) {
            console.log('방법 1: contentRef.current 사용');
            targetElement = contentRef.current;
          } else {
            console.log('방법 1 실패: contentRef.current가 없음');
          }

          // 방법 2: CSS 선택자로 직접 찾기
          if (!targetElement) {
            console.log('방법 2: CSS 선택자로 직접 찾기');
            targetElement = document.querySelector('[data-content]');
            console.log('data-content 요소:', targetElement);
          }

          // 방법 3: 페이지 전체에서 jodit 찾기
          if (!targetElement) {
            console.log('방법 3: 페이지 전체에서 jodit 찾기');
            targetElement = document.querySelector('.jodit-container');
            console.log('jodit-container 요소:', targetElement);
          }

          console.log('최종 선택된 targetElement:', targetElement);

          if (targetElement) {
            // Jodit 에디터 내부의 wysiwyg 영역에서 헤딩 찾기
            const wysiwygElement = targetElement.querySelector('.jodit-wysiwyg');
            console.log('wysiwygElement 찾기 결과:', wysiwygElement);

            // 전체에서도 헤딩 검색해보기
            const allHeadings = targetElement.querySelectorAll('h1, h2, h3, h4');
            console.log('targetElement 전체에서 찾은 헤딩:', allHeadings?.length);

            if (wysiwygElement) {
              const headings = wysiwygElement.querySelectorAll('h1, h2, h3, h4');
              console.log('wysiwyg에서 찾은 헤딩 개수:', headings.length);

              headings.forEach((heading, index) => {
                const level = parseInt(heading.tagName.substring(1));
                const text = heading.textContent?.trim() || '';
                console.log(`헤딩 ${index}:`, { level, text, tagName: heading.tagName });

                if (text && level >= 1 && level <= 4) {
                  // ID 생성
                  const id = `heading-${index}-${text.replace(/\s+/g, '-').toLowerCase().replace(/[^a-z0-9-]/g, '')}`;
                  heading.id = id; // 실제 DOM 요소에 ID 설정

                  tocItems.push({
                    id,
                    text,
                    level
                  });
                }
              });
            } else if (allHeadings && allHeadings.length > 0) {
              // wysiwyg를 찾지 못했지만 전체에서 헤딩을 찾은 경우
              console.log('전체에서 찾은 헤딩 사용:', allHeadings.length);

              allHeadings.forEach((heading, index) => {
                const level = parseInt(heading.tagName.substring(1));
                const text = heading.textContent?.trim() || '';
                console.log(`전체 헤딩 ${index}:`, { level, text, tagName: heading.tagName });

                if (text && level >= 1 && level <= 4) {
                  const id = `heading-${index}-${text.replace(/\s+/g, '-').toLowerCase().replace(/[^a-z0-9-]/g, '')}`;
                  heading.id = id;

                  tocItems.push({
                    id,
                    text,
                    level
                  });
                }
              });
            } else {
              console.log('헤딩을 찾을 수 없음');
            }
          } else {
            console.log('targetElement를 찾을 수 없음');
          }

          console.log('최종 생성된 목차 아이템:', tocItems);
          setToc(tocItems);
        }, 1000); // 에디터 렌더링 완료 대기 (늘림)
      };

      generateTocFromRenderedContent();

      // MutationObserver로 DOM 변화 감지
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
            // Jodit 에디터가 추가되었는지 확인
            const hasJoditElement = Array.from(mutation.addedNodes).some(node =>
              node instanceof Element && (
                node.classList?.contains('jodit-container') ||
                node.querySelector?.('.jodit-container')
              )
            );

            if (hasJoditElement) {
              console.log('Jodit 에디터가 DOM에 추가됨, 목차 재생성');
              setTimeout(() => generateTocFromRenderedContent(), 100);
            }
          }
        });
      });

      // document.body를 관찰하여 Jodit 에디터 추가 감지
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });

      return () => observer.disconnect();
    }
  }, [content]);

  // 스크롤 위치에 따른 활성 헤딩 감지
  useEffect(() => {
    const handleScroll = () => {
      if (!contentRef.current) return;

      // Jodit 에디터 내부의 wysiwyg 영역에서 헤딩 찾기
      const wysiwygElement = contentRef.current.querySelector('.jodit-wysiwyg');
      if (!wysiwygElement) return;

      const headings = wysiwygElement.querySelectorAll('h1, h2, h3, h4');
      let currentHeading = '';

      headings.forEach(heading => {
        const rect = heading.getBoundingClientRect();
        if (rect.top <= 150) { // 상단에서 150px 이내에 있는 헤딩을 활성으로 표시
          currentHeading = heading.id;
        }
      });

      setActiveHeading(currentHeading);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [content]);

  const handleBack = () => {
    navigate('/blog');
  };

  const scrollToHeading = (headingId: string) => {
    setTimeout(() => {
      const element = document.getElementById(headingId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };





  if (loading) {
    return (
      <PageWrapper>
        <Container>
          <LoadingSpinner>블로그 글을 불러오는 중...</LoadingSpinner>
        </Container>
      </PageWrapper>
    );
  }

  if (error || !content) {
    return (
      <PageWrapper>
        <Container>
          <BackButton onClick={handleBack}>
            ← 블로그 목록으로
          </BackButton>
          <ErrorMessage>{error || '블로그 글을 찾을 수 없습니다.'}</ErrorMessage>
        </Container>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <Container>
        <MainContent>
          <BackButton onClick={handleBack}>
            ← 블로그 목록으로
          </BackButton>

          <Header>
            <Title>{content.title}</Title>
            
            <Meta>
              <CategoryBadge>{content.category.name}</CategoryBadge>
              <CreatedAt>{formatDate(content.created_at)}</CreatedAt>
            </Meta>

            {content.meta_description && (
              <MetaDescription>{content.meta_description}</MetaDescription>
            )}

            {content.keywords && content.keywords.length > 0 && (
              <Keywords>
                {content.keywords.map((keyword, index) => (
                  <KeywordTag key={index}>{keyword}</KeywordTag>
                ))}
              </Keywords>
            )}

            {content.author_name && (
              <AuthorSection>
                {content.author_bio && (
                  <AuthorImage src={content.author_bio} alt={content.author_name} />
                )}
                <AuthorInfo>
                  <AuthorName>{content.author_name}</AuthorName>
                  <AuthorRole>작성자</AuthorRole>
                </AuthorInfo>
              </AuthorSection>
            )}
          </Header>

          <ContentWrapper ref={contentRef} data-content>
            <ReadOnlyJoditEditor
              content={content.content}
              height="auto"
            />
          </ContentWrapper>
        </MainContent>

        <Sidebar>
          <SidebarSection>
            <SidebarTitle>목차</SidebarTitle>
            {(() => {
              console.log('목차 렌더링:', { tocLength: toc.length, toc });
              return toc.length > 0 ? (
                <TocList>
                  {toc.map((item) => (
                    <TocItem
                      key={item.id}
                      level={item.level}
                      active={activeHeading === item.id}
                      onClick={() => scrollToHeading(item.id)}
                    >
                      {item.text}
                    </TocItem>
                  ))}
                </TocList>
              ) : (
                <EmptyToc>목차가 없습니다</EmptyToc>
              );
            })()}
          </SidebarSection>

          <SidebarSection>
            <SidebarTitle>관련 글</SidebarTitle>
            {relatedPosts.length > 0 ? (
              <RelatedPostsList>
                {relatedPosts.map((post) => (
                  <RelatedPostItem key={post.id} onClick={() => navigate(`/blog/${post.id}`)}>
                    <RelatedPostItemCategory>{post.category.name}</RelatedPostItemCategory>
                    <RelatedPostItemTitle>{post.title}</RelatedPostItemTitle>
                    <RelatedPostItemMeta>
                      <span>{formatDate(post.created_at)}</span>
                    </RelatedPostItemMeta>
                  </RelatedPostItem>
                ))}
              </RelatedPostsList>
            ) : (
              <EmptyToc>관련된 글이 없습니다</EmptyToc>
            )}
          </SidebarSection>
        </Sidebar>
      </Container>
    </PageWrapper>
  );
};

// 새로운 스타일 컴포넌트
const MainContent = styled.div`
  flex: 1;
  min-width: 0;
  
  @media (max-width: 1024px) {
    order: 1;
  }
`;

const Sidebar = styled.aside`
  width: 280px;
  flex-shrink: 0;
  position: sticky;
  top: 6rem;
  height: fit-content;
  max-height: calc(100vh - 8rem);
  overflow-y: auto;

  /* 스크롤바 스타일링 */
  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 2px;

    &:hover {
      background: rgba(255, 255, 255, 0.5);
    }
  }

  @media (max-width: 1024px) {
    position: static;
    width: 100%;
    order: 2;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    max-height: none;
    overflow-y: visible;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SidebarSection = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: ${theme.borderRadius.lg};
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 1024px) {
    margin-bottom: 0;
  }
`;

const SidebarTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: white;
`;

const TocList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  max-height: 400px;
  overflow-y: auto;

  /* 스크롤바 스타일링 */
  &::-webkit-scrollbar {
    width: 3px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 2px;

    &:hover {
      background: rgba(255, 255, 255, 0.5);
    }
  }
`;

const TocItem = styled.li<{ level: number; active: boolean }>`
  padding: 0.4rem 0.8rem;
  padding-left: ${props => 0.8 + (props.level - 1) * 0.8}rem;
  cursor: pointer;
  color: ${props => props.active ? theme.colors.secondary : 'rgba(255, 255, 255, 0.8)'};
  font-weight: ${props => props.active ? '600' : '400'};
  font-size: ${props => props.level === 1 ? '0.95rem' : props.level === 2 ? '0.9rem' : '0.85rem'};
  transition: all 0.2s ease;
  border-radius: 0.375rem;
  margin-bottom: 0.125rem;
  position: relative;

  &:before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 3px;
    height: ${props => props.active ? '100%' : '0'};
    background: ${theme.colors.secondary};
    border-radius: 0 2px 2px 0;
    transition: height 0.2s ease;
  }

  &:hover {
    color: white;
    background: rgba(255, 255, 255, 0.05);
  }
`;

const RelatedPostsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-height: 500px;
  overflow-y: auto;

  /* 스크롤바 스타일링 */
  &::-webkit-scrollbar {
    width: 3px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 2px;

    &:hover {
      background: rgba(255, 255, 255, 0.5);
    }
  }
`;

const RelatedPostItem = styled.div`
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: ${theme.borderRadius.md};
  border: 1px solid rgba(255, 255, 255, 0.1);
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.3);
    transform: translateX(-4px);
  }
`;

const RelatedPostItemCategory = styled.span`
  font-size: 0.75rem;
  color: ${theme.colors.secondary};
  font-weight: 500;
  text-transform: uppercase;
`;

const RelatedPostItemTitle = styled.h5`
  margin: 0.25rem 0;
  font-size: 0.95rem;
  color: white;
  line-height: 1.3;
  font-weight: 500;
`;

const RelatedPostItemMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.5rem;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.6);
`;



const EmptyToc = styled.div`
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.875rem;
  text-align: center;
  padding: 2rem 0;
  font-style: italic;
`;

export default BlogDetail; 